import { Chord, Note } from 'tonal';

// Enharmonic sharp→flat map for flat-key spellings
const SHARP_TO_FLAT = {
  'A#': 'Bb', 'C#': 'Db', 'D#': 'Eb', 'F#': 'Gb', 'G#': 'Ab',
};

// ---------------------------------------------------------------------------
// Key detection — Krumhansl-Schmuckler profiles (1990)
// ---------------------------------------------------------------------------

// Guitar-preferred pitch class names (index = pitch class 0–11)
const PC_NAMES_MAJOR = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
const PC_NAMES_MINOR = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'G#', 'A', 'Bb', 'B'];

// K-S major and minor key profiles
const KS_MAJOR = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const KS_MINOR = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

/** Pearson correlation of two 12-element arrays */
function pearsonCorr(a, b) {
  const n = 12;
  const meanA = a.reduce((s, x) => s + x, 0) / n;
  const meanB = b.reduce((s, x) => s + x, 0) / n;
  let num = 0, varA = 0, varB = 0;
  for (let i = 0; i < n; i++) {
    const da = a[i] - meanA, db = b[i] - meanB;
    num += da * db; varA += da * da; varB += db * db;
  }
  return varA === 0 || varB === 0 ? 0 : num / Math.sqrt(varA * varB);
}

/**
 * Given a list of chord name strings, estimate the most likely key
 * using Krumhansl-Schmuckler correlation with pitch-class occurrence counts.
 * @param {string[]} chords
 * @returns {string}  e.g. "Bb major" or "A minor"
 */
export function detectKeyFromChords(chords) {
  const pcCount = new Array(12).fill(0);
  for (const name of chords) {
    const parsed = Chord.get(name.split('/')[0]);
    if (!parsed.tonic) continue;
    const pc = Note.midi(parsed.tonic + '4');
    if (pc !== null) pcCount[pc % 12]++;
  }

  if (pcCount.every(v => v === 0)) return '';

  let bestCorr = -2;
  let bestKey = '';

  for (let tonic = 0; tonic < 12; tonic++) {
    // Rotate input profile so `tonic` aligns with index 0
    const rotated = Array.from({ length: 12 }, (_, i) => pcCount[(tonic + i) % 12]);
    const rMaj = pearsonCorr(rotated, KS_MAJOR);
    const rMin = pearsonCorr(rotated, KS_MINOR);
    if (rMaj > bestCorr) { bestCorr = rMaj; bestKey = PC_NAMES_MAJOR[tonic] + ' major'; }
    if (rMin > bestCorr) { bestCorr = rMin; bestKey = PC_NAMES_MINOR[tonic] + ' minor'; }
  }

  return bestKey;
}

/**
 * Convert a MIDI note number to its pitch class (e.g. 60 → "C").
 * Tries both sharp and flat spellings so chord detection has more options.
 */
function midiToPitchClass(midiNote) {
  const sharp = Note.pitchClass(Note.fromMidi(midiNote));
  const flat = SHARP_TO_FLAT[sharp] || sharp;
  return { sharp, flat };
}

/**
 * Normalize a tonal chord name to common guitar notation:
 *   BbM     → Bb        (plain major: strip 'M' suffix)
 *   BbM/F   → Bb/F      (slash major: strip 'M' before '/')
 *   CM7     → Cmaj7     (major 7th: rename to 'maj7' for clarity)
 *   Dm#5/F  → penalized by scoring (unusual alteration + slash)
 */
function normalizeChordName(name) {
  if (!name) return name;
  // Strip standalone major 'M' (not followed by alphanumeric like in 'M7')
  return name
    .replace(/([A-G][b#]?)M(\/|$)/g, '$1$2') // BbM/F → Bb/F, trailing handled next
    .replace(/([A-G][b#]?)M$/, '$1');          // BbM → Bb
}

/**
 * Score a chord name — lower = better / simpler / more guitar-friendly.
 */
function scoreChord(name) {
  let score = 0;
  if (name.includes('/')) score += 8;          // slash chord = less preferred
  if (name.match(/#5|b5|aug|dim7|#11|b13/)) score += 10; // unusual alterations
  if (name.match(/([A-G][b#]?)M($|\/)/)) score += 2;     // unstripped major M (minor penalty)
  // Prefer m7 over 6 (same notes, but m7 is more natural guitar notation)
  if (name.match(/[A-G][b#]?6($|\/)/)) score += 3;
  score += name.replace(/^[A-G][b#]?/, '').length;       // shorter quality = simpler
  return score;
}

/**
 * Pick the best chord name from a list of tonal detections.
 * Tries both sharp and flat spellings AND all note rotations (so every pitch
 * class gets a chance to be the root — Chord.detect is root-order sensitive).
 * After finding the best root chord, re-adds slash notation if the actual
 * bass note (lowest MIDI note) is a chord tone but not the root.
 */
function detectBestChord(midiNotes) {
  if (midiNotes.length < 2) return null;

  const sharpPCs = [...new Set(midiNotes.map(n => midiToPitchClass(n).sharp))];
  const flatPCs  = [...new Set(midiNotes.map(n => midiToPitchClass(n).flat))];

  // Try every rotation so each pitch class can be detected as root
  const allDetected = [];
  for (let i = 0; i < sharpPCs.length; i++) {
    const r = [...sharpPCs.slice(i), ...sharpPCs.slice(0, i)];
    allDetected.push(...Chord.detect(r));
  }
  for (let i = 0; i < flatPCs.length; i++) {
    const r = [...flatPCs.slice(i), ...flatPCs.slice(0, i)];
    allDetected.push(...Chord.detect(r));
  }

  const unique = [...new Set(allDetected)];
  if (unique.length === 0) return null;

  const best = unique
    .map(c => ({ chord: c, score: scoreChord(c) }))
    .sort((a, b) => a.score - b.score)[0].chord;

  const normalized = normalizeChordName(best);

  // Re-add slash notation only when bass note is separated by >12 semitones
  // (genuine bass pedal / walking bass), not just a close-voiced piano inversion
  const sortedNotes = [...midiNotes].sort((a, b) => a - b);
  const bassPC = midiToPitchClass(sortedNotes[0]);
  const gapToNextNote = sortedNotes.length > 1 ? sortedNotes[1] - sortedNotes[0] : 0;

  if (gapToNextNote > 7) {
    const parsed = Chord.get(normalized);
    if (parsed.tonic) {
      const chordPCs = new Set(
        (parsed.notes || []).flatMap(n => {
          const pc = Note.pitchClass(n);
          return [pc, SHARP_TO_FLAT[pc] || pc];
        })
      );
      const bassIsChordTone = chordPCs.has(bassPC.sharp) || chordPCs.has(bassPC.flat);
      const bassIsRoot = bassPC.sharp === parsed.tonic || bassPC.flat === parsed.tonic;
      if (bassIsChordTone && !bassIsRoot) {
        const chordUseFlats = parsed.tonic.includes('b');
        return normalized + '/' + (chordUseFlats ? bassPC.flat : bassPC.sharp);
      }
    }
  }

  return normalized;
}

function readTempoMicrosPerQuarter(event) {
  if (!event) return null;
  const raw = event.data ?? event.metaEventLength ?? event.value;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
  if (ArrayBuffer.isView(raw) && raw.length >= 3) {
    const v = (raw[0] << 16) | (raw[1] << 8) | raw[2];
    return v > 0 ? v : null;
  }
  if (Array.isArray(raw) && raw.length >= 3) {
    const v = (raw[0] << 16) | (raw[1] << 8) | raw[2];
    return v > 0 ? v : null;
  }
  return null;
}

function buildTempoSegments(midiData) {
  const ppq = Number(midiData?.timeDivision) || 480;
  const tempoEvents = [{ tick: 0, mpq: 500000 }];

  (midiData?.track || []).forEach((track) => {
    let tick = 0;
    (track?.event || []).forEach((event) => {
      tick += Number(event?.deltaTime || 0);
      const isTempo = event?.metaType === 81 || event?.metaType === 0x51;
      if (!isTempo) return;
      const mpq = readTempoMicrosPerQuarter(event);
      if (mpq) tempoEvents.push({ tick, mpq });
    });
  });

  tempoEvents.sort((a, b) => a.tick - b.tick);
  const deduped = [];
  for (const evt of tempoEvents) {
    const prev = deduped[deduped.length - 1];
    if (prev && prev.tick === evt.tick) {
      prev.mpq = evt.mpq;
    } else {
      deduped.push({ ...evt });
    }
  }

  const segments = [];
  let sec = 0;
  for (let i = 0; i < deduped.length; i++) {
    const cur = deduped[i];
    const next = deduped[i + 1];
    const tickStart = cur.tick;
    const tickEnd = next ? next.tick : Number.POSITIVE_INFINITY;
    const secPerTick = (cur.mpq / 1_000_000) / ppq;
    if (next) sec += (tickEnd - tickStart) * secPerTick;
    segments.push({
      tickStart,
      tickEnd,
      secStart: i === 0 ? 0 : segments[i - 1].secEnd,
      secEnd: next ? sec : Number.POSITIVE_INFINITY,
      secPerTick,
      mpq: cur.mpq,
    });
  }
  return segments;
}

function tickToSeconds(tick, segments) {
  for (const seg of segments) {
    if (tick >= seg.tickStart && tick < seg.tickEnd) {
      return seg.secStart + (tick - seg.tickStart) * seg.secPerTick;
    }
  }
  const last = segments[segments.length - 1];
  if (!last) return 0;
  return last.secStart + (tick - last.tickStart) * last.secPerTick;
}

/**
 * Parse a MIDI ArrayBuffer and return deduplicated chord names + timed chord events.
 * Groups note-ons within a time window (240 ticks ≈ half a beat at 480 PPQ).
 *
 * @param {ArrayBuffer} arrayBuffer
 * @param {object} MidiParser  - pass in midi-parser-js to avoid circular deps
 * @returns {{ chords: string[], chordEvents: Array<{chord: string, time: number}>, key: string, bpm: number|null }}
 */
export function parseMidiChords(arrayBuffer, MidiParser) {
  const midiData = MidiParser.parse(new Uint8Array(arrayBuffer));
  const tempoSegments = buildTempoSegments(midiData);
  const primaryMpq = tempoSegments[0]?.mpq || 500000;
  const bpm = primaryMpq > 0 ? Number((60_000_000 / primaryMpq).toFixed(2)) : null;
  const notes = [];

  midiData.track.forEach(track => {
    let currentTime = 0;
    track.event.forEach(event => {
      currentTime += event.deltaTime;
      if (event.type === 9 && event.data[1] > 0) {
        notes.push({
          note: event.data[0],
          tick: currentTime,
          time: tickToSeconds(currentTime, tempoSegments),
        });
      }
    });
  });

  notes.sort((a, b) => a.time - b.time);

  // Group notes by time proximity (240 ticks = half beat at 480 PPQ)
  const groups = [];
  let group = [];
  let lastTime = -1;
  const THRESHOLD = 240;

  notes.forEach(n => {
    if (lastTime !== -1 && n.time - lastTime > THRESHOLD) {
      if (group.length > 0) groups.push(group);
      group = [];
    }
    group.push(n);
    lastTime = n.time;
  });
  if (group.length > 0) groups.push(group);

  const chordEvents = [];
  groups.forEach(g => {
    const chord = detectBestChord(g.map(n => n.note));
    if (!chord) return;
    const t = g[0]?.time ?? 0;
    const prev = chordEvents[chordEvents.length - 1];
    // Collapse immediate same-chord repeats from dense MIDI events.
    if (prev && prev.chord === chord && Math.abs(prev.time - t) <= 0.2) return;
    chordEvents.push({ chord, time: Number(t.toFixed(3)) });
  });

  const deduped = chordEvents.map((c) => c.chord);
  const key = detectKeyFromChords(deduped);
  return { chords: deduped, chordEvents, key, bpm };
}
